import nlp from 'compromise';
import { ActiveTemplate } from '../types';

export interface DynamicField {
  key: string;
  label: string;
  placeholder: string;
}

const SKIP = /^(bill to$|client$|date$|due date|time|invoice|receipt|notes|terms|po$|bill no|currency|gstin|dl number|scan|thank|total|subtotal|amount|tax|discount|shipping|payment|bank|account|ifsc|swift|upi|vat|website|http|fssai|www|reg no|license)/i;

const KNOWN = [
  'deliver to','deliver','ship to','recipient','from','sender','attn','attention','patient','age','gender','doctor','waiter','server','table no','table',
  'pharmacist','reg','ref no','ref','ward','bed','diagnosis','order no','order',
  'seat','guest','staff','cashier','operator','batch','expiry','mrp','hsn',
  'serial','token','ticket','room','floor no','section','zone','counter','shift',
  'vehicle','driver','captain','member','loyalty','points','coupon','voucher',
  'booking','reservation','check in','check out','pax','covers','delivery','rider',
  'phone number','phone','mobile','mob no','tel','fax','email','email id',
  'address','adress','addres','addr','contact','whatsapp','website',
];

/**
 * Detects fillable "Label: value/placeholder" fields in a template's text
 * elements. Pulled out of QuickInvoiceView's inline useMemo — same exact
 * pattern set (1–6), just parameterized on `template` instead of closing
 * over component state, so both the single-invoice form and batch mode can
 * call it for whichever template is selected.
 */
export function extractDynamicFields(template: ActiveTemplate): DynamicField[] {
  if (!template) return [];
  const fields: DynamicField[] = [];
  const seen = new Set<string>();

  // Pattern 0: explicit dynamicFieldKey tag — used by templates where the
  // value stands alone with no "Label:" text at all (price tags, barcodes,
  // product QR codes). Bypasses every guessing pattern below.
  for (const el of template.elements as any[]) {
    if (!el.dynamicFieldKey) continue;
    const key = el.dynamicFieldKey;
    if (seen.has(key)) continue;
    seen.add(key);
    const displayLabel = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const currentValue = el.type === 'qr' ? el.qrValue : el.type === 'barcode' ? el.barcodeValue : el.textContent;
    fields.push({ key, label: displayLabel, placeholder: currentValue || `Enter ${displayLabel}` });
  }

  const allText = template.elements
    .filter((el: any) => el.type === 'text' && el.textContent && !/[\u0600-\u06FF]/.test(el.textContent))
    .map((el: any) => el.textContent as string)
    .join('\n');

  const normalized = allText
    .replace(/^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*[-:]+\s*$/gm, '$1:')
    .replace(/^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:-\s*/gm, '$1: ')
    .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:]{0,40}?)\s{2,}(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n')
    .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:|]{0,40}?)\s*\|\s*(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n')
    .replace(/([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n:•]{0,40}?)\s*•\s*(?=[A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?\s*:)/g, '$1: $2\n');

  let m;

  // Pattern 1: "Label: [Placeholder]"
  const bracketPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\[([^\]]{0,40})\]/gm;
  while ((m = bracketPattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    const rawValue = m[2].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) {
      seen.add(key);
      fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` });
    }
  }

  // Pattern 2: "Label: __"
  const underscorePattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*_{2,}/gm;
  while ((m = underscorePattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
  }

  // Pattern 2b: dash placeholder
  const dashPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*[-—]{1,3}\s*$/gm;
  while ((m = dashPattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
  }

  // Pattern 2c: ellipsis placeholder
  const ellipsisPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\.{2,}|…\s*$/gm;
  while ((m = ellipsisPattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
  }

  // Pattern 2d: symbol placeholders
  const symbolPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*[#?*~x]{2,}\s*$/gm;
  while ((m = symbolPattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
  }

  // Pattern 2e: curly brace placeholder
  const curlyPattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*\{([^}]{0,40})\}/gm;
  while ((m = curlyPattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    const rawValue = m[2].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` }); }
  }

  // Pattern 2f: angle bracket placeholder
  const anglePattern = /([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*<([^>]{0,40})>/gm;
  while ((m = anglePattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    const rawValue = m[2].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: rawValue || `Enter ${rawLabel}` }); }
  }

  // Pattern 3: bare label
  const barePattern = /^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*$/gm;
  while ((m = barePattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: `Enter ${rawLabel}` }); }
  }

  // Pattern 4: baked-in short values
  const bakedPattern = /^([A-Za-z][A-Za-z\s\.#\/\(\)]{1,25}?)\s*:\s*([^\n\[\]_]{1,50}?)$/gm;
  while ((m = bakedPattern.exec(normalized)) !== null) {
    const rawLabel = m[1].trim();
    const rawValue = m[2].trim();
    if (SKIP.test(rawLabel) || rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const wordCount = rawValue.split(/\s+/).filter(Boolean).length;
    if (wordCount > 4) continue;
    if (/\d{5,}|floor|suite|avenue|street|road|nagar|layout|bangalore|mumbai|delhi|london|new york/i.test(rawValue)) continue;
    if (/^(inc|ltd|llc|pvt|corp|co\.|gstin|fssai|dl:|mob:|www\.|http)/i.test(rawValue)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
    if (!seen.has(key)) { seen.add(key); fields.push({ key, label: rawLabel, placeholder: rawValue }); }
  }

  // Pattern 5: known field keywords
  for (const keyword of KNOWN) {
    const keywordRegex = new RegExp(
      `\\b(${keyword.replace(/\s/g, '[\\s\\.]*')})\\s*:\\s*([^\\n:]{0,40}?)(?=\\s{2,}[A-Za-z]|\\n|$)`,
      'gi'
    );
    while ((m = keywordRegex.exec(allText)) !== null) {
      const rawLabel = m[1].trim();
      const rawValue = (m[2] || '').trim();
      if (SKIP.test(rawLabel)) continue;
      const key = rawLabel.toLowerCase().replace(/[\s\.#\/\(\)]+/g, '_');
      if (!seen.has(key)) {
        seen.add(key);
        const displayLabel = rawLabel.replace(/\b\w/g, c => c.toUpperCase());
        fields.push({
          key,
          label: displayLabel,
          placeholder: rawValue && !/^\[|^_+$/.test(rawValue) ? rawValue : `Enter ${displayLabel}`,
        });
      }
    }
  }

  // Pattern 6: compromise NLP catch-all
  try {
    const doc = nlp(allText);
    const nounPhrases: string[] = doc.nouns().out('array') as string[];
    for (const phrase of nounPhrases) {
      const clean = phrase.trim().toLowerCase();
      if (clean.split(' ').length > 2) continue;
      const labelRegex = new RegExp(`\\b${clean.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`, 'i');
      if (!labelRegex.test(allText)) continue;
      if (SKIP.test(clean)) continue;
      const key = clean.replace(/\s+/g, '_');
      if (!seen.has(key)) {
        seen.add(key);
        const displayLabel = phrase.trim().replace(/\b\w/g, c => c.toUpperCase());
        fields.push({ key, label: displayLabel, placeholder: `Enter ${displayLabel}` });
      }
    }
  } catch { /* compromise failed silently */ }

  return fields;
}

/** Same prefill logic QuickInvoiceView runs on template change — pulls
 * baked-in values out of the template text as starting defaults. Exposed
 * separately since batch mode doesn't need prefill (every field comes
 * from the CSV row), but single-invoice mode still does. */
export function prefillDynamicValues(template: ActiveTemplate): Record<string, string> {
  if (!template) return {};
  const prefilled: Record<string, string> = {};
  const allText = template.elements
    .filter((el: any) => el.type === 'text' && el.textContent)
    .map((el: any) => el.textContent as string)
    .join('\n');

  const pairPattern = /([A-Za-z][A-Za-z\s\/\.\(\)#]{1,25}?)\s*:\s*([^:\n]{0,40}?)(?=\s{2,}[A-Za-z]|\n|$)/g;
  let match;
  const skip = /^(bill to|client|date|due date|time|invoice|receipt|notes|terms|po|bill no|from|sender|attn|attention|currency|settlement|creation|gstin|dl|scan|thank|hsr|layout|http|www|total|subtotal|amount|vat|tax|discount|shipping|payment|bank|account|ifsc|swift|upi)/i;
  while ((match = pairPattern.exec(allText)) !== null) {
    const rawLabel = match[1].trim();
    const rawValue = (match[2] || '').trim();
    if (skip.test(rawLabel)) continue;
    if (rawLabel.length < 2 || /\d/.test(rawLabel)) continue;
    const wordCount = rawValue.split(/\s+/).filter(Boolean).length;
    if (wordCount > 5) continue;
    if (/floor|suite|ave|street|road|nagar|layout|\d{5,}/i.test(rawValue)) continue;
    const key = rawLabel.toLowerCase().replace(/[\s\/\.\(\)#]+/g, '_');
    if (rawValue && !/^\[|^__/.test(rawValue)) {
      prefilled[key] = rawValue;
    }
  }
  return prefilled;
}